import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

/**
 * Methods that allow values to be initialized.
 */

/**
 * An extension point that can have one or more value registered with it, and can return its
 * value or collection of values.
 */

/**
 * An extension point that has a single value.
 */

/**
 * An async version of SingleValueExtensionPoint that allows the value to be
 * computed with an async function.
 */

/**
 * An extension point that has multiple values.
 */

/**
 * An extension point that has multiple values, each generated with an async function.
 */

/**
 * An extension point that maps keys to values.
 */

/**
 * An extension point that can dispatch events to zero or more registered listeners.
 */

/**
 * An extension point that can dispatch events to zero or more registered listeners, asynchronously.
 */

function uninitialized() {
  throw new Error("Extension point is uninitialized");
}

// extension point with a single value
class SyncSingletonExtensionPoint {
  constructor(initialValue) {
    _defineProperty(this, "getter", void 0);
    _defineProperty(this, "registry", {
      register: factory => {
        this.getter = () => {
          const value = factory();
          this.getter = () => value;
          return value;
        };
        return this.registry;
      }
    });
    this.getter = initialValue ?? uninitialized;
  }

  // get the value
  get() {
    return this.getter();
  }

  // Always holds exactly one value (even if register has never been called).
  // Calling get() before register throws, so it's invalid to have less than 1 value.
  get size() {
    return 1;
  }
}

// base an extension points that are lists of values
class SyncArrayExtensionPointBase {
  constructor() {
    _defineProperty(this, "values", []);
    _defineProperty(this, "registry", {
      // add a function that computes a value on demand
      register: factory => {
        const element = new SyncSingletonExtensionPoint();
        element.registry.register(factory);
        this.addNewElement(element);
        return this.registry;
      }
    });
  }
  getAllValues() {
    return this.values.map(extensionPoint => extensionPoint.get());
  }

  // Number of elements in the array.  It's possible to get the length synchronously,
  // even if the array is an array of promises.
  get size() {
    return this.values.length;
  }
  addNewElement(element) {
    this.values.push(element);
  }
}

// an extension point that is a list of values
class SyncArrayExtensionPoint extends SyncArrayExtensionPointBase {
  get() {
    return this.getAllValues();
  }
}

// an extension point that is a list of values, possibly computed asynchronously
class ArrayExtensionPoint extends SyncArrayExtensionPointBase {
  get() {
    return Promise.all(this.getAllValues());
  }
}
class SyncEventDispatcherArrayExtensionPoint extends SyncArrayExtensionPoint {
  dispatch(event) {
    for (const extensionPoint of this.values) {
      const listener = extensionPoint.get();
      listener(event);
    }
  }
}
class SyncMapExtensionPoint {
  constructor(keyFunction) {
    _defineProperty(this, "extensionMap", new Map());
    // values that are meant to be lazy loaded, but are not loaded yet.
    // they can't be placed into extensionMap, since the key is unknown (needs to be derived from the value).
    // queue them here, then resolve them on operations that are needed.
    _defineProperty(this, "unresolvedValues", []);
    _defineProperty(this, "keyFunction", void 0);
    _defineProperty(this, "registry", {
      // add a function that computes a value on demand
      register: factory => {
        // the value isn't known, so add it to a queue to eventually
        // be placed into the map
        this.unresolvedValues.push(factory);
        return this.registry;
      }
    });
    this.keyFunction = keyFunction;
  }

  // Number of values currently in map plus those not yet in map.
  get size() {
    return this.extensionMap.size + this.unresolvedValues.length;
  }

  // Retrieve the current value of the extension point.
  // For an extension point representing multiple values, this will return an array of values.

  // return the value for a given key, or undefined if this value doesn't exist.

  // union of the two above
  get(key) {
    return key === undefined ? this.getMap() : this.getValue(key);
  }
  getMap() {
    this.resolveAllValues();
    const valueMap = new Map();
    for (const [key, value] of this.extensionMap.entries()) {
      valueMap.set(key, value.get());
    }
    return valueMap;
  }
  getValue(key) {
    this.resolveAllValues();
    const extension = this.extensionMap.get(key);
    return extension?.get();
  }

  // work through the queue of all values that haven't yet been created,
  // adding them to the map
  resolveAllValues() {
    const queue = this.unresolvedValues;
    while (queue.length > 0) {
      const factory = queue.pop();
      if (factory) {
        const value = factory();
        this.indexValue(value);
      }
    }
  }
  indexValue(value) {
    // retrieve key
    const key = this.keyFunction(value);
    let element = this.extensionMap.get(key);
    // create extension for this key if it doesn't exist yet
    if (!element) {
      element = new SyncSingletonExtensionPoint();
      this.extensionMap.set(key, element);
    }
    element.registry.register(() => value);
  }
}
class EventDispatcherArrayExtensionPoint extends ArrayExtensionPoint {
  dispatch(event) {
    return Promise.all(this.values.map(async extensionPoint => {
      const listener = await extensionPoint.get();
      await listener(event);
    }));
  }
}
export class ExtensionPoints {
  static syncSingleValue(initialValue) {
    return new SyncSingletonExtensionPoint(initialValue);
  }
  static singleValue(initialValue) {
    return new SyncSingletonExtensionPoint(initialValue);
  }
  static syncMultiValue() {
    return new SyncArrayExtensionPoint();
  }
  static multiValue() {
    return new ArrayExtensionPoint();
  }
  static syncEventDispatcher() {
    return new SyncEventDispatcherArrayExtensionPoint();
  }
  static eventDispatcher() {
    return new EventDispatcherArrayExtensionPoint();
  }
  static syncKeyedValues(keyFunction) {
    return new SyncMapExtensionPoint(keyFunction);
  }
}
//# sourceMappingURL=ExtensionPoints.js.map
